import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { asyncScheduler, EMPTY, merge, Observable, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    expand,
    filter,
    map,
    mergeMap,
    observeOn,
    reduce,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    collapseAccordionByGroup,
    collapseAccordionWithChildren,
    expandAccordionByGroup,
    expandAccordionWithChildren,
    registerAccordionGroups,
} from '../../store/actions/form.actions';
import { updateRemarksSection } from '../../store/actions/functional-elements.actions';
import {
    getForm,
    getFormAnswers,
    getFormContentByIdFactory,
    getFormDefectServicePartIdentification,
    getFormDefectServicePartIdentificationValid,
    getFormHeadings,
    getFormHintsForFilling,
    getFormIsLoading,
    getFormParticipantCopyReadonly,
    getFormSystemCondition,
    getFormTypeFilters,
    getMeasuringEquipments,
    getRemarksSection,
    getSystemConditionValidity,
    getTopLevelFormContent,
} from '../../store/selectors/form.selector';
import { FormState } from '../../store/state/form.state';
import { FormsState } from '../../store/state/pace-forms.state';
import { ContentBlock } from '../../types/content-block';
import { CONTENT_TYPE } from '../../types/content-child';
import { FormsConstants } from '../../types/forms-constants';
import { HintsForFilling } from '../../types/hints-for-filling';
import { RemarksSection } from '../../types/remarks-section';
import { SystemCondition } from '../../types/system-condition';
import { FormUtilities } from '../../utilities/form-utilities';
import { MeasuringEquipments } from '../../types/measuring-equipments';
import { EnvironmentService } from '@evorait/pace-environment';

@Component({
    selector: 'pace-forms2-renderer',
    templateUrl: './forms2-renderer.component.html',
    styleUrls: ['./forms2-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class Forms2RendererComponent implements AfterViewInit, OnInit, OnDestroy {
    public form$: Observable<FormState>;
    public isLoading$: Observable<boolean>;
    public defectServicePartIdentificationHeading$: Observable<string>;
    public customerInformationHeading$: Observable<string>;
    public remarksSection$: Observable<RemarksSection | undefined>;
    public content$: Observable<ContentBlock[]>;
    public systemCondition$: Observable<SystemCondition | undefined>;
    public hintsForFilling$: Observable<HintsForFilling | undefined>;
    public displaySystemCondition$: Observable<boolean>;
    public displayHintsForFilling$: Observable<boolean>;
    public isSystemConditionValid$: Observable<boolean>;
    public notServicePartQuestionnaire$: Observable<boolean>;
    public measurementEquipments$: Observable<MeasuringEquipments>;
    public readOnly$: Observable<boolean>;
    public paceFormsChildren: FormArray;

    public systemConditionChildren: FormArray;
    public defectServicePartIdentificationChildren: FormArray;
    public paceFormsGroup: FormGroup;
    private destroy = new Subject<void>();
    public defectServicePartIdentificationOpen = false;

    public customerIdentificationOpen = false;
    public hintsForFillingOpen = false;
    public remarksSectionOpen = false;
    public systemConditionOpen = false;
    public xmlViewerOpen = false;
    public readonly isMockMode: boolean;

    public remarksSectionControl: FormControl;

    public isDefectServicePartIdentificationInvalid$: Observable<boolean>;
    public constructor(
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private action$: Actions,
        private readonly environmentService: EnvironmentService,
    ) {
        this.isMockMode = this.environmentService.operateInMockMode;

        this.form$ = store.select(getForm).pipe(take(1));
        this.isLoading$ = store.select(getFormIsLoading).pipe(take(1));
        this.content$ = store.select(getTopLevelFormContent);
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);

        this.customerInformationHeading$ = this.form$.pipe(
            map(
                (form) =>
                    form.title +
                    (form.variant.length > 0 ? `: ${form.variant}` : '') +
                    (form.formId.formId.length > 0 ? ` (${form.formId.formId})` : ''),
            ),
        );

        this.defectServicePartIdentificationHeading$ = this.store.select(getFormDefectServicePartIdentification).pipe(
            filter(isDefined),
            withLatestFrom(this.form$),
            map(
                ([defectServicePartIdentification, form]) =>
                    `${defectServicePartIdentification.title} (${form.formId.formId})`,
            ),
        );

        this.measurementEquipments$ = this.store.pipe(
            select(getMeasuringEquipments),
            filter(isDefined),
            takeUntil(this.destroy),
        );

        this.systemCondition$ = store.select(getFormSystemCondition).pipe(take(1));
        this.displaySystemCondition$ = this.systemCondition$.pipe(map(isDefined), take(1));
        this.hintsForFilling$ = store.select(getFormHintsForFilling).pipe(take(1));
        this.remarksSection$ = store.select(getRemarksSection).pipe(take(1));
        this.displayHintsForFilling$ = store.select(getFormHintsForFilling).pipe(map(isDefined), take(1));
        this.isSystemConditionValid$ = store.select(getSystemConditionValidity).pipe(map((valid) => !valid));
        this.isDefectServicePartIdentificationInvalid$ = store.select(getFormDefectServicePartIdentificationValid).pipe(
            map((valid) => !valid),
            tap(() => this.cdr.detectChanges()),
        );

        this.notServicePartQuestionnaire$ = this.form$.pipe(
            map((form) => !FormUtilities.isServicePartQuestionnaire(form.type)),
        );

        this.paceFormsChildren = this.formBuilder.array([]);
        this.systemConditionChildren = this.formBuilder.array([]);
        this.defectServicePartIdentificationChildren = this.formBuilder.array([]);

        this.paceFormsGroup = this.formBuilder.group({
            children: this.paceFormsChildren,
            systemCondition: this.systemConditionChildren,
            defectServicePartIdentification: this.defectServicePartIdentificationChildren,
        });

        this.systemConditionChildren.statusChanges
            .pipe(distinctUntilChanged(), takeUntil(this.destroy))
            .subscribe(() => {
                this.cdr.detectChanges();
            });

        this.defectServicePartIdentificationChildren.statusChanges
            .pipe(distinctUntilChanged(), takeUntil(this.destroy))
            .subscribe(() => {
                this.cdr.detectChanges();
            });

        this.content$
            .pipe(
                take(1),
                mergeMap((x) => x),
                expand((contentBlock) =>
                    this.store.select(getFormContentByIdFactory({ id: contentBlock.id })).pipe(
                        take(1),
                        mergeMap((x) => (x && x.children && x.children.length > 0 ? x.children : EMPTY)),
                    ),
                ),
                filter((child) => child.type === CONTENT_TYPE.HEADING),
                withLatestFrom(this.store.select(getFormHeadings)),
                map(([heading, headings]) => headings[heading.id]),
                filter(isDefined),
                map(
                    (headingElement) =>
                        `${FormsConstants.SECTION_TEXT}-${headingElement.chapterNumber}-${headingElement.id}`,
                ),
                reduce((acc, curr) => [...acc, curr], [] as string[]),
                takeUntil(this.destroy),
            )
            .subscribe((groupIds: string[]) => {
                this.store.dispatch(registerAccordionGroups({ groupIds }));
            });
    }

    public ngOnInit(): void {
        this.remarksSectionControl = this.formBuilder.control('');

        // Don't try this at home this is an anti pattern to trigger the change detection on those actions
        const groupExpandedChanged$ = this.action$.pipe(
            ofType(
                expandAccordionByGroup,
                collapseAccordionByGroup,
                expandAccordionWithChildren,
                collapseAccordionWithChildren,
            ),
            filter((action) => action.groupId !== `${FormsConstants.SECTION_TEXT}-*`),
        );

        // The async scheduler is needed as the checkpoints subscribe later and therefore will also receive emissions later
        const filterChanged$ = this.store.select(getFormTypeFilters).pipe(observeOn(asyncScheduler));
        const answers$ = this.store.select(getFormAnswers).pipe(observeOn(asyncScheduler));

        merge(groupExpandedChanged$, filterChanged$, answers$)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                this.cdr.detectChanges();
            });

        this.remarksSection$
            .pipe(withLatestFrom(this.readOnly$), takeUntil(this.destroy))
            .subscribe(([remarksSection, readOnly]) => {
                this.remarksSectionControl[!readOnly ? 'enable' : 'disable']();
                this.remarksSectionControl.patchValue(remarksSection?.value);
            });

        this.remarksSectionControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.remarksSection$.pipe(take(1))),
                takeUntil(this.destroy),
            )
            .subscribe(([newValue, remarksSection]: [string, RemarksSection]) => {
                if (!isDefined(remarksSection?.remarksXmlId)) {
                    throw new Error(
                        `Forms2RendererComponent: remarksSectionControl valueChanges: RemarksSection has no xml Id!`,
                    );
                }

                this.store.dispatch(
                    updateRemarksSection({
                        xmlId: remarksSection?.remarksXmlId ?? '',
                        newValue,
                    }),
                );
            });
    }

    public ngOnDestroy(): void {
        this.destroy.next();
    }

    public ngAfterViewInit(): void {
        this.cdr.detach();
    }

    public expandAll(): void {
        this.store.dispatch(
            expandAccordionByGroup({
                groupId: `${FormsConstants.SECTION_TEXT}-*`,
            }),
        );
        this.cdr.detectChanges();
    }

    public collapseAll(): void {
        this.store.dispatch(
            collapseAccordionByGroup({
                groupId: `${FormsConstants.SECTION_TEXT}-*`,
            }),
        );
        this.cdr.detectChanges();
    }

    public trackByFn(index: number, item: ContentBlock): number {
        return item.id;
    }

    public defectServicePartIdentificationOpenChanged(open: boolean): void {
        this.defectServicePartIdentificationOpen = open;
        this.cdr.detectChanges();
    }

    public customerIdentificationOpenChanged(open: boolean): void {
        this.customerIdentificationOpen = open;
        this.cdr.detectChanges();
    }

    public hintsForFillingOpenChanged(open: boolean): void {
        this.hintsForFillingOpen = open;
        this.cdr.detectChanges();
    }

    public remarksSectionOpenChanged(open: boolean): void {
        this.remarksSectionOpen = open;
        this.cdr.detectChanges();
    }

    public systemConditionOpenChanged(open: boolean): void {
        this.systemConditionOpen = open;
        this.cdr.detectChanges();
    }

    public xmlViewerOpenChanged(open: boolean): void {
        this.xmlViewerOpen = open;
        this.cdr.detectChanges();
    }
}
